import {
  Music, FileText, Mic, Palette, ShoppingBag, UtensilsCrossed, Ticket, Headphones,
  Check, ExternalLink,
} from "lucide-react";

interface VenueOfficialServicesProps {
  venue: any;
}

const services = [
  { key: "audio_recording", label: "Grabación audio", icon: Mic },
  { key: "poster_design", label: "Cartelería", icon: Palette },
  { key: "merch_area_official", label: "Merchandising", icon: ShoppingBag },
  { key: "catering_official", label: "Catering", icon: UtensilsCrossed },
  { key: "sound_tech_official", label: "Técnico de sonido", icon: Headphones },
];

function getServiceState(val: boolean | null | undefined) {
  if (val === true) return { text: "Sí", type: "yes" as const };
  if (val === false) return { text: "No", type: "no" as const };
  return { text: "Desconocido", type: "unknown" as const };
}

function isValidUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string") return null;
  let u = url.trim();
  if (!u) return null;
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;
  try {
    new URL(u);
    return u;
  } catch {
    return null;
  }
}

export function VenueOfficialServices({ venue }: VenueOfficialServicesProps) {
  const hasAnyData =
    venue.backline_available != null || venue.rider_available != null ||
    services.some((s) => venue[s.key] != null) || venue.ticketing_type != null;
  if (!hasAnyData) return null;

  // Ticketing state
  const ticketingText =
    venue.ticketing_type === "obligatorio"
      ? "Propio (obligatorio)"
      : venue.ticketing_type === "libre"
      ? "Libre"
      : null;
  const ticketingState = ticketingText
    ? { text: ticketingText, type: "yes" as const }
    : { text: "Desconocido", type: "unknown" as const };

  // Merged Backline / Rider tile
  const backlineRiderValue = venue.backline_available ?? venue.rider_available ?? null;
  const backlineRiderState = getServiceState(backlineRiderValue);
  const backlineRiderUrl = isValidUrl(venue.backline_url) ?? isValidUrl(venue.rider_url);

  const allItems = [
    {
      label: "Backline / Rider",
      icon: Music,
      state: backlineRiderState,
      url: backlineRiderUrl,
      linkLabel: backlineRiderUrl ? "Ver recurso" : null,
    },
    ...services.map(({ key, label, icon }) => ({
      label,
      icon,
      state: getServiceState(venue[key]),
      url: null,
      linkLabel: null,
    })),
    {
      label: "Ticketing",
      icon: Ticket,
      state: ticketingState,
      url: null,
      linkLabel: null,
    },
  ];

  return (
    <section>
      <h2 className="mb-4 font-display text-xl font-bold">Servicios oficiales</h2>
      <div className="grid grid-cols-2 gap-3">
        {allItems.map(({ label, icon: Icon, state, url, linkLabel }) => {
          const isYes = state.type === "yes";
          const isUnknown = state.type === "unknown";

          return (
            <div
              key={label}
              className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                isYes
                  ? "border-success/30 bg-success/5"
                  : isUnknown
                  ? "border-border bg-muted/30"
                  : "border-border"
              }`}
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  isYes
                    ? "bg-success/15 text-success"
                    : isUnknown
                    ? "bg-muted text-muted-foreground/50"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-tight">{label}</p>
                <p
                  className={`text-[11px] ${
                    isUnknown ? "text-muted-foreground/50" : "text-muted-foreground"
                  }`}
                >
                  {state.text}
                </p>
                {url && linkLabel && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                  >
                    {linkLabel}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {isYes && (
                <Check className="h-4 w-4 shrink-0 text-success" />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
